import { getApiUrl } from '@/lib/utils'
import type { StandardCondition } from '@/types/inventory'
import type { CardmarketPrice, Locale } from '@/types/pokemon'

const API_URL = getApiUrl()

export const CarmarketLanguageId = {
  English: 1,
  German: 3,
  Japanese: 7,
} as const

export type CarmarketLanguageId = (typeof CarmarketLanguageId)[keyof typeof CarmarketLanguageId]

const CARDMARKET_LANGUAGE_BY_LOCALE: Record<Locale, CarmarketLanguageId> = {
  en: CarmarketLanguageId.English,
  de: CarmarketLanguageId.German,
  ja: CarmarketLanguageId.Japanese,
}

export function getCardmarketLanguageIdByLocale(locale: Locale): CarmarketLanguageId {
  return CARDMARKET_LANGUAGE_BY_LOCALE[locale]
}

export const CardmarketConditionId = {
  Mint: 1,
  NearMint: 2,
  Excellent: 3,
  Good: 4,
  LightPlayed: 5,
  Played: 6,
  Poor: 7,
} as const

export type CardmarketConditionId = (typeof CardmarketConditionId)[keyof typeof CardmarketConditionId]

const CARDMARKET_CONDITION_BY_STANDARD: Record<StandardCondition, CardmarketConditionId> = {
  Mint: CardmarketConditionId.Mint,
  NearMint: CardmarketConditionId.NearMint,
  Excellent: CardmarketConditionId.Excellent,
  Good: CardmarketConditionId.Good,
  Played: CardmarketConditionId.Played,
  Poor: CardmarketConditionId.Poor,
}

/** Maps inventory `StandardCondition` names to Cardmarket filter/API condition ids. */
export function getCardmarketConditionIdByName(condition: StandardCondition): CardmarketConditionId {
  return CARDMARKET_CONDITION_BY_STANDARD[condition]
}

export type CardmarketFilter = {
  languages: Locale[]
  minCondition: StandardCondition
}

export function createCardmarketFilterUrl(cardUrl: string, filter?: CardmarketFilter): string {
  const cardmarketMinCondition = filter && filter.minCondition ? getCardmarketConditionIdByName(filter.minCondition) : undefined
  const filterValues = []
  if (filter?.languages) {
    filterValues.push(`language=${filter.languages.map(getCardmarketLanguageIdByLocale).join(',')}`)
  }
  if (cardmarketMinCondition && cardmarketMinCondition != CardmarketConditionId.Poor) {
    filterValues.push(`minCondition=${cardmarketMinCondition}`)
  }
  return `${cardUrl}?${filterValues.join('&')}`
}

export async function fetchCardmarketPrices(cardId: string, condition: StandardCondition): Promise<CardmarketPrice[]> {
  const response = await fetch(`${API_URL}/api/cardmarket/prices?cardId=${encodeURIComponent(cardId)}&condition=${encodeURIComponent(condition)}`, {
    credentials: 'include',
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Failed to load Cardmarket prices (${response.status})`)
  }
  return data as CardmarketPrice[]
}
